import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const Practice = () => {
  // Tabs: 'languages' or 'problems'
  const [activeTab, setActiveTab] = useState('languages');

  // Lists
  const [languages, setLanguages] = useState([]);
  const [problems, setProblems] = useState([]);
  const [selectedLanguageSlug, setSelectedLanguageSlug] = useState('');

  // Loading & Messages
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modals state
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState(null);
  const [editingProblem, setEditingProblem] = useState(null);

  // Problem Wizard tab: 'basic', 'description', 'testcases', 'templates'
  const [problemWizardTab, setProblemWizardTab] = useState('basic');

  // API base URL
  const apiBase = API_BASE_URL;
  console.log('Practice API Base URL:', apiBase);

  // Language Form State
  const [langForm, setLangForm] = useState({
    name: '',
    slug: '',
    icon: '',
    description: '',
    difficulty: 'Beginner',
    color: '#3b82f6',
    order: 0,
    learnersCount: 0,
    tags: '',
    features: '',
    isActive: true,
  });

  // Problem Form State
  const [probForm, setProbForm] = useState({
    title: '',
    slug: '',
    description: '',
    language: '',
    difficulty: 'Easy',
    acceptance: 0,
    timeLimit: '1s',
    memoryLimit: '256MB',
    order: 0,
    isActive: true,
    tags: '',
    constraints: '',
    hints: '',
    examples: [{ input: '', output: '', explanation: '' }],
    testCases: [{ input: '', expectedOutput: '', isHidden: false }],
    codeTemplates: {
      cpp: '',
      python: '',
      java: '',
      javascript: '',
    },
  });

  // Fetch languages
  const fetchLanguages = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${apiBase}/api/practice/languages`);
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('API server returned HTML instead of JSON. Please check if the backend server is running and your REACT_APP_API_URL environment variable is correct.');
      }
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch languages');
      }
      setLanguages(data.data || []);
      if (data.data && data.data.length > 0 && !selectedLanguageSlug) {
        setSelectedLanguageSlug(data.data[0].slug);
      }
    } catch (err) {
      setError(err.message || 'Error loading languages');
    } finally {
      setLoading(false);
    }
  };

  // Fetch problems by language slug
  const fetchProblems = async (slug) => {
    if (!slug) return;
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      };
      const res = await fetch(`${apiBase}/api/practice/languages/${slug}/problems`, { headers });
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        throw new Error('API server returned HTML instead of JSON. Please check if the backend server is running and your REACT_APP_API_URL environment variable is correct.');
      }
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to fetch problems');
      }
      setProblems(data.data || []);
    } catch (err) {
      setError(err.message || 'Error loading problems');
      setProblems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    if (activeTab === 'problems' && selectedLanguageSlug) {
      fetchProblems(selectedLanguageSlug);
    }
  }, [activeTab, selectedLanguageSlug]);

  // Toast auto-dismiss helper
  const showToastSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 5000);
  };

  const showToastError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 6000);
  };

  // Generate slug automatically
  const handleLanguageNameChange = (e) => {
    const name = e.target.value;
    const generatedSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setLangForm((prev) => ({ ...prev, name, slug: generatedSlug }));
  };

  const handleProblemTitleChange = (e) => {
    const title = e.target.value;
    const generatedSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    setProbForm((prev) => ({ ...prev, title, slug: generatedSlug }));
  };

  // Open Add Language modal
  const handleOpenAddLanguage = () => {
    setEditingLanguage(null);
    setLangForm({
      name: '',
      slug: '',
      icon: '💻',
      description: '',
      difficulty: 'Beginner',
      color: '#3b82f6',
      order: 0,
      learnersCount: 0,
      tags: '',
      features: '',
      isActive: true,
    });
    setShowLanguageModal(true);
  };

  // Open Edit Language modal
  const handleOpenEditLanguage = (lang) => {
    setEditingLanguage(lang);
    setLangForm({
      name: lang.name,
      slug: lang.slug,
      icon: lang.icon || '💻',
      description: lang.description || '',
      difficulty: lang.difficulty || 'Beginner',
      color: lang.color || '#3b82f6',
      order: lang.order || 0,
      learnersCount: lang.learnersCount || 0,
      tags: Array.isArray(lang.tags) ? lang.tags.join(', ') : '',
      features: Array.isArray(lang.features) ? lang.features.join(', ') : '',
      isActive: lang.isActive !== undefined ? lang.isActive : true,
    });
    setShowLanguageModal(true);
  };

  // Open Add Problem modal
  const handleOpenAddProblem = () => {
    setEditingProblem(null);
    setProblemWizardTab('basic');
    
    // Auto-select active language if possible
    const selectedLangObj = languages.find(l => l.slug === selectedLanguageSlug);
    
    setProbForm({
      title: '',
      slug: '',
      description: '',
      language: selectedLangObj ? selectedLangObj._id : '',
      difficulty: 'Easy',
      acceptance: 0,
      timeLimit: '1s',
      memoryLimit: '256MB',
      order: 0,
      isActive: true,
      tags: '',
      constraints: '',
      hints: '',
      examples: [{ input: '', output: '', explanation: '' }],
      testCases: [{ input: '', expectedOutput: '', isHidden: false }],
      codeTemplates: {
        cpp: '',
        python: '',
        java: '',
        javascript: '',
      },
    });
    setShowProblemModal(true);
  };

  // Open Edit Problem modal
  const handleOpenEditProblem = (prob) => {
    setEditingProblem(prob);
    setProblemWizardTab('basic');
    setProbForm({
      title: prob.title,
      slug: prob.slug,
      description: prob.description || '',
      language: prob.language?._id || prob.language || '',
      difficulty: prob.difficulty || 'Easy',
      acceptance: prob.acceptance || 0,
      timeLimit: prob.timeLimit || '1s',
      memoryLimit: prob.memoryLimit || '256MB',
      order: prob.order || 0,
      isActive: prob.isActive !== undefined ? prob.isActive : true,
      tags: Array.isArray(prob.tags) ? prob.tags.join(', ') : '',
      constraints: Array.isArray(prob.constraints) ? prob.constraints.join(', ') : '',
      hints: Array.isArray(prob.hints) ? prob.hints.join(', ') : '',
      examples: prob.examples && prob.examples.length > 0 ? prob.examples : [{ input: '', output: '', explanation: '' }],
      testCases: prob.testCases && prob.testCases.length > 0 ? prob.testCases : [{ input: '', expectedOutput: '', isHidden: false }],
      codeTemplates: {
        cpp: prob.codeTemplates?.cpp || '',
        python: prob.codeTemplates?.python || '',
        java: prob.codeTemplates?.java || '',
        javascript: prob.codeTemplates?.javascript || '',
      },
    });
    setShowProblemModal(true);
  };

  // Handle submit Language
  const handleSubmitLanguage = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    const payload = {
      ...langForm,
      tags: langForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      features: langForm.features.split(',').map(f => f.trim()).filter(Boolean),
    };

    try {
      let res;
      if (editingLanguage) {
        res = await fetch(`${apiBase}/api/practice/admin/language/${editingLanguage._id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${apiBase}/api/practice/admin/language`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to save language');
      }

      showToastSuccess(editingLanguage ? 'Language updated successfully' : 'Language created successfully');
      setShowLanguageModal(false);
      fetchLanguages();
    } catch (err) {
      showToastError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete Language
  const handleDeleteLanguage = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will delete the language and ALL associated problems!`)) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      };
      const res = await fetch(`${apiBase}/api/practice/admin/language/${id}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete language');
      }
      showToastSuccess('Language and associated problems deleted successfully');
      fetchLanguages();
    } catch (err) {
      showToastError(err.message || 'Error deleting language');
    } finally {
      setLoading(false);
    }
  };

  // Handle submit Problem
  const handleSubmitProblem = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    // Construct request payload
    const payload = {
      ...probForm,
      tags: probForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      constraints: probForm.constraints.split(',').map(c => c.trim()).filter(Boolean),
      hints: probForm.hints.split(',').map(h => h.trim()).filter(Boolean),
    };

    try {
      let res;
      if (editingProblem) {
        res = await fetch(`${apiBase}/api/practice/admin/problem/${editingProblem._id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${apiBase}/api/practice/admin/problem`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        });
      }

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to save problem');
      }

      showToastSuccess(editingProblem ? 'Problem updated successfully' : 'Problem created successfully');
      setShowProblemModal(false);
      fetchProblems(selectedLanguageSlug);
      // Reload languages to get updated problemCounts
      fetchLanguages();
    } catch (err) {
      showToastError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete Problem
  const handleDeleteProblem = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete problem "${title}"?`)) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      };
      const res = await fetch(`${apiBase}/api/practice/admin/problem/${id}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to delete problem');
      }
      showToastSuccess('Problem deleted successfully');
      fetchProblems(selectedLanguageSlug);
      // Reload languages to get updated problemCounts
      fetchLanguages();
    } catch (err) {
      showToastError(err.message || 'Error deleting problem');
    } finally {
      setLoading(false);
    }
  };

  // Dynamic Array Helper functions
  const addExample = () => {
    setProbForm(prev => ({
      ...prev,
      examples: [...prev.examples, { input: '', output: '', explanation: '' }],
    }));
  };

  const removeExample = (index) => {
    setProbForm(prev => ({
      ...prev,
      examples: prev.examples.filter((_, idx) => idx !== index),
    }));
  };

  const handleExampleChange = (index, field, value) => {
    const updated = [...probForm.examples];
    updated[index][field] = value;
    setProbForm(prev => ({ ...prev, examples: updated }));
  };

  const addTestCase = () => {
    setProbForm(prev => ({
      ...prev,
      testCases: [...prev.testCases, { input: '', expectedOutput: '', isHidden: false }],
    }));
  };

  const removeTestCase = (index) => {
    setProbForm(prev => ({
      ...prev,
      testCases: prev.testCases.filter((_, idx) => idx !== index),
    }));
  };

  const handleTestCaseChange = (index, field, value) => {
    const updated = [...probForm.testCases];
    if (field === 'isHidden') {
      updated[index][field] = value;
    } else {
      updated[index][field] = value;
    }
    setProbForm(prev => ({ ...prev, testCases: updated }));
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 min-h-[600px]">
      {/* Title section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-6 border-b border-gray-100 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Practice Module Manager</h2>
          <p className="text-gray-500 text-sm mt-1">Configure practice languages, coding problems, test cases, and templates.</p>
        </div>
        <div className="mt-4 md:mt-0 flex space-x-3">
          {activeTab === 'languages' ? (
            <button
              onClick={handleOpenAddLanguage}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center transition shadow-sm"
            >
              <span className="mr-1.5 text-lg font-bold">+</span> Add Language
            </button>
          ) : (
            <button
              onClick={handleOpenAddProblem}
              disabled={languages.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="mr-1.5 text-lg font-bold">+</span> Add Problem
            </button>
          )}
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded flex justify-between items-center transition">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-900 font-bold ml-4">✕</button>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 rounded flex justify-between items-center transition">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-900 font-bold ml-4">✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('languages')}
          className={`py-2.5 px-6 font-semibold text-sm border-b-2 transition ${
            activeTab === 'languages'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          🗂️ Languages ({languages.length})
        </button>
        <button
          onClick={() => setActiveTab('problems')}
          className={`py-2.5 px-6 font-semibold text-sm border-b-2 transition ${
            activeTab === 'problems'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          💻 Coding Problems
        </button>
      </div>

      {/* Tab content */}
      {loading && languages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <span>Loading data...</span>
        </div>
      ) : activeTab === 'languages' ? (
        /* LANGUAGES LISTING */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {languages.length === 0 ? (
            <div className="col-span-full py-16 text-center text-gray-400 bg-gray-50 rounded-xl border border-dashed">
              <span className="text-4xl block mb-2">📦</span>
              <p className="font-semibold text-gray-600">No languages found</p>
              <p className="text-sm mt-1">Get started by creating your first programming language.</p>
              <button
                onClick={handleOpenAddLanguage}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm transition"
              >
                + Add Language
              </button>
            </div>
          ) : (
            languages.map((lang) => (
              <div
                key={lang._id}
                className="border border-gray-100 rounded-xl bg-white p-5 shadow-sm hover:shadow-md transition relative flex flex-col justify-between"
                style={{ borderTop: `4px solid ${lang.color || '#3b82f6'}` }}
              >
                <div>
                  <div className="flex justify-between items-start">
                  
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      lang.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {lang.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mt-4 flex items-center">
                    {lang.name}
                    <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {lang.difficulty}
                    </span>
                  </h3>
                  <p className="text-xs text-gray-400 mt-0.5">Slug: <span className="font-mono">{lang.slug}</span></p>
                  <p className="text-gray-600 text-sm mt-3 line-clamp-3 leading-relaxed">{lang.description}</p>
                  
                  {/* Metadata info */}
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-50 text-xs">
                    <div>
                      <span className="text-gray-400 block">Problems</span>
                      <span className="font-bold text-gray-800 text-sm">{lang.problemCount || 0} Problems</span>
                    </div>
                   
                  </div>
                </div>

                <div className="flex space-x-2 mt-5 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handleOpenEditLanguage(lang)}
                    className="flex-1 bg-gray-50 hover:bg-gray-100 text-gray-700 py-1.5 rounded text-xs font-semibold transition text-center"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleDeleteLanguage(lang._id, lang.name)}
                    className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 py-1.5 rounded text-xs font-semibold transition text-center"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* PROBLEMS LISTING */
        <div>
          {/* Language selector dropdown */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 bg-gray-50 p-4 rounded-xl border">
            <div className="flex items-center space-x-3 w-full sm:w-auto">
              <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">Filter by Language:</label>
              <select
                value={selectedLanguageSlug}
                onChange={(e) => setSelectedLanguageSlug(e.target.value)}
                className="bg-white border rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-60"
              >
                {languages.map((l) => (
                  <option key={l._id} value={l.slug}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-xs text-gray-500 font-mono">
              Total problems in this view: {problems.length}
            </div>
          </div>

          {problems.length === 0 ? (
            <div className="py-20 text-center text-gray-400 bg-gray-50 rounded-xl border border-dashed">
              <span className="text-4xl block mb-2">📋</span>
              <p className="font-semibold text-gray-600">No problems found for this language</p>
              <p className="text-sm mt-1">Get started by creating a practice problem.</p>
              <button
                onClick={handleOpenAddProblem}
                disabled={languages.length === 0}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm transition disabled:opacity-50"
              >
                + Add Problem
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase bg-gray-50">
                    <th className="py-3 px-4 font-bold">Order / No.</th>
                    <th className="py-3 px-4">Title</th>
                    <th className="py-3 px-4">Difficulty</th>
                    <th className="py-3 px-4">Acceptance %</th>
                    <th className="py-3 px-4">Limits</th>
                    <th className="py-3 px-4">Testcases</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {problems.map((prob) => (
                    <tr key={prob._id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-mono font-semibold text-gray-600">
                        #{prob.order || 0}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-bold text-gray-900">{prob.title}</div>
                        <div className="text-xs text-gray-400 font-mono">{prob.slug}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                          prob.difficulty === 'Easy'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : prob.difficulty === 'Medium'
                            ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {prob.difficulty || 'Easy'}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-semibold text-gray-700">
                        {prob.acceptance || 0}%
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-600">
                        ⏱️ {prob.timeLimit || '1s'} <br />
                        💾 {prob.memoryLimit || '256MB'}
                      </td>
                      <td className="py-3 px-4 text-xs font-semibold text-gray-700">
                        {(prob.testCases || []).length} items
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                          prob.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {prob.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleOpenEditProblem(prob)}
                            className="bg-white hover:bg-gray-100 border text-gray-700 px-3 py-1.5 rounded text-xs font-semibold transition"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteProblem(prob._id, prob.title)}
                            className="bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded text-xs font-semibold transition"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* LANGUAGE MODAL */}
      {showLanguageModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border w-full max-w-xl overflow-hidden transition-all transform scale-100">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">
                {editingLanguage ? '✏️ Edit Language' : '➕ Add Language'}
              </h3>
              <button
                onClick={() => setShowLanguageModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmitLanguage} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Language Name*</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. C++"
                    value={langForm.name}
                    onChange={handleLanguageNameChange}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Slug*</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. cpp"
                    value={langForm.slug}
                    onChange={(e) => setLangForm(prev => ({ ...prev, slug: e.target.value }))}
                    className="w-full border bg-gray-55 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Color Theme (Hex)*</label>
                  <input
                    type="text"
                    required
                    placeholder="#3b82f6"
                    value={langForm.color}
                    onChange={(e) => setLangForm(prev => ({ ...prev, color: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Difficulty Level*</label>
                  <select
                    value={langForm.difficulty}
                    onChange={(e) => setLangForm(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                    <option value="Expert">Expert</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Description*</label>
                <textarea
                  required
                  rows="3"
                  placeholder="Describe the language and what it teaches..."
                  value={langForm.description}
                  onChange={(e) => setLangForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Sort Order (Number)</label>
                  <input
                    type="number"
                    value={langForm.order}
                    onChange={(e) => setLangForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
             
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Tags (Comma-separated)</label>
                <input
                  type="text"
                  placeholder="OOP, Dynamic Typing, Web Development"
                  value={langForm.tags}
                  onChange={(e) => setLangForm(prev => ({ ...prev, tags: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Features (Comma-separated)</label>
                <input
                  type="text"
                  placeholder="Pointers, Fast performance, Memory safety"
                  value={langForm.features}
                  onChange={(e) => setLangForm(prev => ({ ...prev, features: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center space-x-3 pt-2">
                <input
                  type="checkbox"
                  id="langIsActive"
                  checked={langForm.isActive}
                  onChange={(e) => setLangForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="h-4.5 w-4.5 border text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="langIsActive" className="text-sm font-semibold text-gray-700">
                  Language is Active and Available to users
                </label>
              </div>

              {/* Modal footer */}
              <div className="pt-6 border-t flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowLanguageModal(false)}
                  className="border px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition"
                >
                  {loading ? 'Saving...' : 'Save Language'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PROBLEM MODAL */}
      {showProblemModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border w-full max-w-4xl overflow-hidden transition-all transform scale-100 flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-gray-50 border-b flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {editingProblem ? '✏️ Edit Problem' : '➕ Add Problem'}
                </h3>
                <p className="text-xs text-gray-400 font-mono mt-0.5">{probForm.slug || 'no-slug-yet'}</p>
              </div>
              <button
                onClick={() => setShowProblemModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>

            {/* Wizard Tabs Navigation */}
            <div className="bg-gray-50 border-b flex space-x-1 px-4 text-xs font-semibold text-gray-500">
              <button
                type="button"
                onClick={() => setProblemWizardTab('basic')}
                className={`py-3 px-4 border-b-2 transition ${
                  problemWizardTab === 'basic' ? 'border-blue-600 text-blue-600 font-bold' : 'border-transparent hover:text-gray-700'
                }`}
              >
                1. Basic Info
              </button>
              <button
                type="button"
                onClick={() => setProblemWizardTab('description')}
                className={`py-3 px-4 border-b-2 transition ${
                  problemWizardTab === 'description' ? 'border-blue-600 text-blue-600 font-bold' : 'border-transparent hover:text-gray-700'
                }`}
              >
                2. Details & Metadata
              </button>
              <button
                type="button"
                onClick={() => setProblemWizardTab('testcases')}
                className={`py-3 px-4 border-b-2 transition ${
                  problemWizardTab === 'testcases' ? 'border-blue-600 text-blue-600 font-bold' : 'border-transparent hover:text-gray-700'
                }`}
              >
                3. Examples & Test Cases ({(probForm.testCases || []).length})
              </button>
              <button
                type="button"
                onClick={() => setProblemWizardTab('templates')}
                className={`py-3 px-4 border-b-2 transition ${
                  problemWizardTab === 'templates' ? 'border-blue-600 text-blue-600 font-bold' : 'border-transparent hover:text-gray-700'
                }`}
              >
                4. Code Templates
              </button>
            </div>

            {/* Modal Body / Form Container */}
            <form onSubmit={handleSubmitProblem} className="flex-1 overflow-y-auto p-6 flex flex-col justify-between">
              
              {/* PAGE 1: BASIC INFO */}
              {problemWizardTab === 'basic' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Problem Title*</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Reverse a String"
                        value={probForm.title}
                        onChange={handleProblemTitleChange}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Slug*</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. reverse-a-string"
                        value={probForm.slug}
                        onChange={(e) => setProbForm(prev => ({ ...prev, slug: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Language mapping*</label>
                      <select
                        required
                        value={probForm.language}
                        onChange={(e) => setProbForm(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                      >
                        <option value="">-- Select Language --</option>
                        {languages.map((l) => (
                          <option key={l._id} value={l._id}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Difficulty level*</label>
                      <select
                        required
                        value={probForm.difficulty}
                        onChange={(e) => setProbForm(prev => ({ ...prev, difficulty: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                      >
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Sort Order (Number)</label>
                      <input
                        type="number"
                        value={probForm.order}
                        onChange={(e) => setProbForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Time Limit*</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. 1s"
                        value={probForm.timeLimit}
                        onChange={(e) => setProbForm(prev => ({ ...prev, timeLimit: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Memory Limit*</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. 256MB"
                        value={probForm.memoryLimit}
                        onChange={(e) => setProbForm(prev => ({ ...prev, memoryLimit: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Acceptance rate (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={probForm.acceptance}
                        onChange={(e) => setProbForm(prev => ({ ...prev, acceptance: parseInt(e.target.value) || 0 }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 pt-2">
                    <input
                      type="checkbox"
                      id="probIsActive"
                      checked={probForm.isActive}
                      onChange={(e) => setProbForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="h-4.5 w-4.5 border text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="probIsActive" className="text-sm font-semibold text-gray-700">
                      Problem is Active and Visible to users
                    </label>
                  </div>
                </div>
              )}

              {/* PAGE 2: DETAILS & METADATA */}
              {problemWizardTab === 'description' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Problem Description (Supports Markdown)*</label>
                    <textarea
                      required
                      rows="6"
                      placeholder="Write the problem statement, format, and input/output descriptions..."
                      value={probForm.description}
                      onChange={(e) => setProbForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Tags (Comma-separated)</label>
                    <input
                      type="text"
                      placeholder="e.g. Arrays, String Manipulation, Dynamic Programming"
                      value={probForm.tags}
                      onChange={(e) => setProbForm(prev => ({ ...prev, tags: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Constraints (Comma-separated list)</label>
                    <textarea
                      rows="2"
                      placeholder="e.g. 1 <= input.length <= 10^5, Value of element can be negative"
                      value={probForm.constraints}
                      onChange={(e) => setProbForm(prev => ({ ...prev, constraints: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Hints (Comma-separated list)</label>
                    <textarea
                      rows="2"
                      placeholder="e.g. Try sorting the array first, Think about double pointers"
                      value={probForm.hints}
                      onChange={(e) => setProbForm(prev => ({ ...prev, hints: e.target.value }))}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              )}

              {/* PAGE 3: EXAMPLES & TEST CASES */}
              {problemWizardTab === 'testcases' && (
                <div className="space-y-6">
                  {/* Examples Section */}
                  <div>
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100 mb-3">
                      <h4 className="text-sm font-bold text-gray-800">📖 User-Facing Examples</h4>
                      <button
                        type="button"
                        onClick={addExample}
                        className="bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1 rounded text-xs font-bold transition"
                      >
                        + Add Example
                      </button>
                    </div>

                    {probForm.examples.map((ex, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-3 relative space-y-3">
                        <button
                          type="button"
                          onClick={() => removeExample(index)}
                          className="absolute top-2 right-3 text-red-500 hover:text-red-700 text-xs font-bold"
                        >
                          Remove
                        </button>
                        <span className="text-xs font-bold text-gray-400 block">Example {index + 1}</span>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-0.5">Input</label>
                            <textarea
                              rows="1"
                              required
                              placeholder='e.g. nums = [2,7,11,15], target = 9'
                              value={ex.input}
                              onChange={(e) => handleExampleChange(index, 'input', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-xs focus:outline-none font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-0.5">Output</label>
                            <textarea
                              rows="1"
                              required
                              placeholder='e.g. [0,1]'
                              value={ex.output}
                              onChange={(e) => handleExampleChange(index, 'output', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-xs focus:outline-none font-mono"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-0.5">Explanation</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. Because nums[0] + nums[1] == 9, we return [0, 1]"
                            value={ex.explanation}
                            onChange={(e) => handleExampleChange(index, 'explanation', e.target.value)}
                            className="w-full border rounded px-2 py-1 text-xs focus:outline-none"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Test Cases Section */}
                  <div>
                    <div className="flex justify-between items-center pb-2 border-b border-gray-100 mb-3">
                      <h4 className="text-sm font-bold text-gray-800">🧪 Compilation Test Cases</h4>
                      <button
                        type="button"
                        onClick={addTestCase}
                        className="bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1 rounded text-xs font-bold transition"
                      >
                        + Add Test Case
                      </button>
                    </div>

                    {probForm.testCases.map((tc, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-3 relative space-y-3">
                        <button
                          type="button"
                          onClick={() => removeTestCase(index)}
                          className="absolute top-2 right-3 text-red-500 hover:text-red-700 text-xs font-bold"
                        >
                          Remove
                        </button>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-bold text-gray-400">Test Case {index + 1}</span>
                          <span className="text-xs text-gray-300">|</span>
                          <label className="inline-flex items-center text-xs text-gray-600 font-semibold cursor-pointer">
                            <input
                              type="checkbox"
                              checked={tc.isHidden}
                              onChange={(e) => handleTestCaseChange(index, 'isHidden', e.target.checked)}
                              className="mr-1 h-3.5 w-3.5 border rounded"
                            />
                            Hidden Test Case (Not shown to user)
                          </label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-0.5">Input Values (stdin)</label>
                            <textarea
                              rows="2"
                              required
                              placeholder="e.g. 2\n7\n11\n15"
                              value={tc.input}
                              onChange={(e) => handleTestCaseChange(index, 'input', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-xs focus:outline-none font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-0.5">Expected Output (stdout)</label>
                            <textarea
                              rows="2"
                              required
                              placeholder="e.g. 0 1"
                              value={tc.expectedOutput}
                              onChange={(e) => handleTestCaseChange(index, 'expectedOutput', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-xs focus:outline-none font-mono"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PAGE 4: CODE TEMPLATES */}
              {problemWizardTab === 'templates' && (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500 leading-relaxed mb-2">
                    Define the initial code template that users will see when they select a programming language. You can leave templates blank if you want the editor to be clean.
                  </p>
                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">C++ Template</label>
                    <textarea
                      rows="3"
                      placeholder="e.g. #include <iostream>..."
                      value={probForm.codeTemplates.cpp}
                      onChange={(e) => setProbForm(prev => ({
                        ...prev,
                        codeTemplates: { ...prev.codeTemplates, cpp: e.target.value }
                      }))}
                      className="w-full border rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Python Template</label>
                    <textarea
                      rows="3"
                      placeholder="e.g. def solve()..."
                      value={probForm.codeTemplates.python}
                      onChange={(e) => setProbForm(prev => ({
                        ...prev,
                        codeTemplates: { ...prev.codeTemplates, python: e.target.value }
                      }))}
                      className="w-full border rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Java Template</label>
                    <textarea
                      rows="3"
                      placeholder="e.g. public class Main..."
                      value={probForm.codeTemplates.java}
                      onChange={(e) => setProbForm(prev => ({
                        ...prev,
                        codeTemplates: { ...prev.codeTemplates, java: e.target.value }
                      }))}
                      className="w-full border rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-gray-500 mb-1">JavaScript Template</label>
                    <textarea
                      rows="3"
                      placeholder="e.g. function main()..."
                      value={probForm.codeTemplates.javascript}
                      onChange={(e) => setProbForm(prev => ({
                        ...prev,
                        codeTemplates: { ...prev.codeTemplates, javascript: e.target.value }
                      }))}
                      className="w-full border rounded-lg px-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono bg-gray-50"
                    />
                  </div>
                </div>
              )}

              {/* Modal footer */}
              <div className="pt-6 border-t flex justify-between items-center mt-6">
                <div>
                  {problemWizardTab !== 'basic' && (
                    <button
                      type="button"
                      onClick={() => {
                        if (problemWizardTab === 'templates') setProblemWizardTab('testcases');
                        else if (problemWizardTab === 'testcases') setProblemWizardTab('description');
                        else if (problemWizardTab === 'description') setProblemWizardTab('basic');
                      }}
                      className="border px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition"
                    >
                      ← Previous
                    </button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowProblemModal(false)}
                    className="border px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  {problemWizardTab !== 'templates' ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (problemWizardTab === 'basic') setProblemWizardTab('description');
                        else if (problemWizardTab === 'description') setProblemWizardTab('testcases');
                        else if (problemWizardTab === 'testcases') setProblemWizardTab('templates');
                      }}
                      className="bg-gray-800 hover:bg-gray-900 text-white px-5 py-2 rounded-lg text-sm font-semibold transition"
                    >
                      Next Step →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm font-semibold transition"
                    >
                      {loading ? 'Saving...' : 'Save Problem'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Practice;
